import { SystemLocation } from './sbc-api.service';
import { extractGeoJsonRing } from './geo.util';

type GeoJsonLike = {
  type?: string;
  coordinates?: unknown;
  geometry?: GeoJsonLike;
  properties?: Record<string, unknown>;
  features?: GeoJsonLike[];
};

export interface ParsedBoundaryFeature {
  index: number;
  properties: Record<string, unknown>;
  boundaryGeojson: Record<string, unknown>;
  matchKeys: string[];
}

export interface BulkBoundaryMatch {
  featureIndex: number;
  plot: SystemLocation;
  boundaryGeojson: Record<string, unknown>;
  matchKey: string;
}

export interface BulkBoundaryPreview {
  matches: BulkBoundaryMatch[];
  unmatchedFeatures: { index: number; matchKeys: string[]; properties: Record<string, unknown> }[];
  unmatchedPlots: SystemLocation[];
  duplicatePlotIds: number[];
}

const PROPERTY_KEYS = [
  'khasra_number',
  'khasra',
  'Khasra',
  'KHASRA',
  'plot_code',
  'code',
  'location_code',
  'khasra_no',
  'khasraNo',
  'plotCode',
  'locationCode',
];

export function normalizeMatchToken(raw: string): string[] {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return [];

  const upper = trimmed.toUpperCase();
  const tokens = new Set<string>([upper]);

  const digits = trimmed.replace(/^KHASRA[-_\s]?/i, '').replace(/\D/g, '');
  if (digits) {
    tokens.add(`KHASRA-${digits}`);
    tokens.add(digits);
  }

  return [...tokens];
}

export function plotMatchKeys(plot: SystemLocation): string[] {
  const keys = new Set<string>();
  if (plot.code) normalizeMatchToken(plot.code).forEach((k) => keys.add(k));
  const khasra = plot.attributes?.['khasra_number'];
  if (khasra != null) normalizeMatchToken(String(khasra)).forEach((k) => keys.add(k));
  return [...keys];
}

export function extractFeatureMatchKeys(properties: Record<string, unknown> | undefined): string[] {
  const keys = new Set<string>();
  if (!properties) return [];

  for (const key of PROPERTY_KEYS) {
    const value = properties[key];
    if (value == null || String(value).trim() === '') continue;
    normalizeMatchToken(String(value)).forEach((k) => keys.add(k));
  }

  return [...keys];
}

function geometryToPolygonGeoJson(geometry: GeoJsonLike | null | undefined): Record<string, unknown> | null {
  if (!geometry?.type) return null;

  if (geometry.type === 'Polygon') {
    const ring = extractGeoJsonRing(geometry);
    return ring && ring.length >= 3 ? { type: 'Polygon', coordinates: [ring] } : null;
  }

  if (geometry.type === 'MultiPolygon') {
    const coords = geometry.coordinates;
    if (!Array.isArray(coords) || !Array.isArray(coords[0]) || !Array.isArray(coords[0][0])) return null;
    const ring = coords[0][0] as number[][];
    return ring.length >= 3 ? { type: 'Polygon', coordinates: [ring] } : null;
  }

  return null;
}

function parseSingleFeature(raw: unknown, index: number): ParsedBoundaryFeature | null {
  if (!raw || typeof raw !== 'object') return null;
  const feature = raw as GeoJsonLike;

  if (feature.type === 'Feature') {
    const boundaryGeojson = geometryToPolygonGeoJson(feature.geometry);
    if (!boundaryGeojson) return null;
    const properties = (feature.properties || {}) as Record<string, unknown>;
    return {
      index,
      properties,
      boundaryGeojson,
      matchKeys: extractFeatureMatchKeys(properties),
    };
  }

  if (feature.type === 'Polygon' || feature.type === 'MultiPolygon') {
    const boundaryGeojson = geometryToPolygonGeoJson(feature);
    if (!boundaryGeojson) return null;
    const properties = (feature.properties || {}) as Record<string, unknown>;
    return {
      index,
      properties,
      boundaryGeojson,
      matchKeys: extractFeatureMatchKeys(properties),
    };
  }

  return null;
}

export function parseGeoJsonBoundaryFeatures(geojson: unknown): ParsedBoundaryFeature[] {
  if (!geojson || typeof geojson !== 'object') return [];
  const root = geojson as GeoJsonLike;

  if (root.type === 'FeatureCollection' && Array.isArray(root.features)) {
    const out: ParsedBoundaryFeature[] = [];
    root.features.forEach((feature, idx) => {
      const parsed = parseSingleFeature(feature, idx);
      if (parsed) out.push(parsed);
    });
    return out;
  }

  const single = parseSingleFeature(root, 0);
  return single ? [single] : [];
}

export function previewBulkBoundaryImport(
  features: ParsedBoundaryFeature[],
  plots: SystemLocation[]
): BulkBoundaryPreview {
  const plotKeyMap = new Map<string, SystemLocation>();
  for (const plot of plots) {
    for (const key of plotMatchKeys(plot)) {
      if (!plotKeyMap.has(key)) plotKeyMap.set(key, plot);
    }
  }

  const matches: BulkBoundaryMatch[] = [];
  const unmatchedFeatures: BulkBoundaryPreview['unmatchedFeatures'] = [];
  const matchedPlotIds = new Set<number>();
  const duplicatePlotIds = new Set<number>();

  for (const feature of features) {
    if (!feature.matchKeys.length) {
      unmatchedFeatures.push({
        index: feature.index,
        matchKeys: [],
        properties: feature.properties,
      });
      continue;
    }

    let plot: SystemLocation | undefined;
    let matchKey = '';
    for (const key of feature.matchKeys) {
      const candidate = plotKeyMap.get(key);
      if (candidate) {
        plot = candidate;
        matchKey = key;
        break;
      }
    }

    if (!plot) {
      unmatchedFeatures.push({
        index: feature.index,
        matchKeys: feature.matchKeys,
        properties: feature.properties,
      });
      continue;
    }

    if (matchedPlotIds.has(plot.id)) {
      duplicatePlotIds.add(plot.id);
    }
    matchedPlotIds.add(plot.id);

    matches.push({
      featureIndex: feature.index,
      plot,
      boundaryGeojson: feature.boundaryGeojson,
      matchKey,
    });
  }

  const unmatchedPlots = plots.filter((p) => !matchedPlotIds.has(p.id));

  return {
    matches,
    unmatchedFeatures,
    unmatchedPlots,
    duplicatePlotIds: [...duplicatePlotIds],
  };
}
